import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Handshake, Search, Star, Check, ExternalLink, Building2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { logoUrl } from '../mockData';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

export const AccordoLogo = ({ accordo, size = 'w-16 h-16' }) => {
  const [failed, setFailed] = useState(false);
  if (!accordo.logo_url || failed) {
    return (
      <div className={`${size} rounded-xl bg-[#2B7A9F]/10 flex items-center justify-center flex-shrink-0`}>
        <Building2 className="text-[#2B7A9F]" size={28} />
      </div>
    );
  }
  return (
    <img
      src={accordo.logo_url}
      alt={`Logo ${accordo.nome}`}
      onError={() => setFailed(true)}
      className={`${size} rounded-xl object-contain bg-white border border-gray-100 flex-shrink-0`}
    />
  );
};

const AccordoCard = ({ accordo }) => (
  <div
    data-testid={`accordo-card-${accordo.slug}`}
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 p-6 flex flex-col relative"
  >
    {accordo.in_primo_piano && (
      <span className="absolute -top-3 left-5 inline-flex items-center gap-1 bg-[#F9D424] text-gray-900 text-xs font-bold px-3 py-1 rounded-full shadow">
        <Star size={12} fill="currentColor" /> In primo piano
      </span>
    )}
    <div className="flex items-start gap-4 mb-4">
      <AccordoLogo accordo={accordo} />
      <div className="min-w-0">
        <h3 className="font-bold text-gray-800 leading-snug">{accordo.nome}</h3>
        <span className="inline-block mt-1 text-xs font-medium text-[#2B7A9F] bg-[#2B7A9F]/10 px-2.5 py-0.5 rounded-full">
          {accordo.categoria}
        </span>
      </div>
    </div>
    {accordo.descrizione && (
      <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 mb-4">{accordo.descrizione}</p>
    )}
    {accordo.vantaggi && accordo.vantaggi.length > 0 && (
      <ul className="space-y-1.5 mb-5">
        {accordo.vantaggi.slice(0, 3).map((v, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
            <Check size={15} className="text-[#5FB76E] flex-shrink-0 mt-0.5" />
            <span>{v}</span>
          </li>
        ))}
      </ul>
    )}
    <div className="mt-auto flex items-center gap-3 pt-2">
      <a
        data-testid={`accordo-cta-${accordo.slug}`}
        href={accordo.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2B7A9F] hover:bg-[#256b8c] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors duration-300"
      >
        Vai all'Accordo <ExternalLink size={14} />
      </a>
      <Link
        to={`/rete-di-accordi/${accordo.slug}`}
        className="text-sm text-[#2B7A9F] hover:text-[#3AAFA9] font-medium underline whitespace-nowrap"
      >
        Dettagli
      </Link>
    </div>
  </div>
);

export default function ReteAccordi() {
  const navigate = useNavigate();
  const [accordi, setAccordi] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('tutte');

  useEffect(() => {
    document.title = 'Rete di Accordi — Assietica';
    fetch(`${API}/api/rete-accordi`)
      .then((r) => r.json())
      .then((data) => {
        setAccordi(data.accordi || []);
        setCategorie(data.categorie || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = accordi.filter((a) => {
      const matchSearch = a.nome.toLowerCase().includes(search.toLowerCase());
      const matchCat = categoria === 'tutte' || a.categoria === categoria;
      return matchSearch && matchCat;
    });
    return [...list].sort((a, b) => {
      if (a.in_primo_piano !== b.in_primo_piano) return a.in_primo_piano ? -1 : 1;
      return a.nome.localeCompare(b.nome, 'it');
    });
  }, [accordi, search, categoria]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#2B7A9F] hover:text-[#3AAFA9] transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Torna alla Home</span>
            </button>
            <img src={logoUrl} alt="Assietica" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-r from-[#2B7A9F] via-[#3AAFA9] to-[#5FB76E] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6">
            <Handshake className="text-white" size={38} />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Rete di Accordi</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Gli Accordi di Maggior Favore riservati a soci, dipendenti e aderenti degli Enti convenzionati
            con Assietica. Trova il tuo ente e accedi alle condizioni dedicate con il codice ricevuto.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1">
        {/* Barra ricerca e filtri */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="accordi-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome dell'ente..."
              className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F]"
            />
          </div>
          <select
            data-testid="accordi-categoria-select"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B7A9F] sm:w-64"
          >
            <option value="tutte">Tutte le categorie</option>
            {categorie.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">Caricamento accordi...</div>
        ) : filtered.length === 0 ? (
          <div data-testid="accordi-empty-state" className="text-center py-20">
            <Handshake size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">
              {accordi.length === 0
                ? 'I primi accordi saranno pubblicati a breve.'
                : 'Nessun accordo corrisponde alla ricerca.'}
            </p>
            {accordi.length === 0 && (
              <p className="text-gray-400 text-sm mt-2">
                Torna a trovarci oppure scrivici a agenzia@assietica.it per informazioni.
              </p>
            )}
          </div>
        ) : (
          <div data-testid="accordi-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-3">
            {filtered.map((a) => (
              <AccordoCard key={a.id} accordo={a} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
